/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

import useQuery from "./useQuery";
import { KioskScale, ratingBand } from "./KioskScale";
import {
  KIOSK_OK,
  KIOSK_ERR_ALREADY,
  KIOSK_ERR_CLOSED,
  buildSurveyUrl,
  createRequest,
  fetchSurveys,
  pickSurvey,
  sendRatingResilient,
} from "./kioskApi";
import {
  loadStore,
  outboxDropFirst,
  outboxDropLast,
  outboxEnqueue,
  outboxPeek,
  outboxRehomeFirst,
  outboxRehomeLast,
  poolPop,
  poolPush,
  POOL_MAX,
  saveStore,
  setQuestion,
} from "./kioskStore";

import "./KioskNps.scss";

/*
 * Totem de NPS para tablet em modo quiosque. Porte de konfidency.nps-kiosk
 * (firmware ESP32-S3). A configuracao vem toda pela URL, porque num tablet o
 * Fully Kiosk ja e dono da rede, do PIN e da trava de saida — as sete telas de
 * manutencao do firmware nao precisam existir aqui.
 *
 * As quatro invariantes do firmware que sao de produto, nao de hardware:
 *  1. a nota e persistida e a tela de agradecimento sobe ANTES do POST;
 *  2. partial e sempre true, senao a API recusa o complemento feito pelo QR;
 *  3. o userId e um nonce novo a cada solicitacao (get-or-create);
 *  4. HTTP 400 reemite o requestId, nao descarta a nota.
 */

const DEFAULT_COUNTDOWN_SECONDS = 30;
const DEFAULT_POOL_TARGET = 5;
const MAINTENANCE_MS = 30000;
const ERROR_RETRY_MS = 15000;

const ERROR_NETWORK = "network";
const ERROR_CONFIG = "config";

function clampParam(raw, fallback, min, max) {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return fallback;

  return Math.min(Math.max(Math.trunc(value), min), max);
}

export function KioskNps() {
  const { t, i18n } = useTranslation();
  const { customer } = useParams();
  const query = useQuery();

  const surveyId = query.get("surveyId") || "";
  const forcedLanguage = query.get("lang");

  /* Os numeros vem da URL, que e digitada a mao na configuracao do Fully Kiosk.
   * O teto da reserva nao e cosmetico: acima de POOL_MAX o poolPush passa a
   * ignorar em silencio e o refill viraria um laco infinito de POSTs. */
  const countdownSeconds = clampParam(query.get("countdown"), DEFAULT_COUNTDOWN_SECONDS, 1, 600);
  const poolTarget = clampParam(query.get("pool"), DEFAULT_POOL_TARGET, 1, POOL_MAX);

  const [screen, setScreen] = useState("boot");
  const [question, setQuestionText] = useState("");
  const [customerInfo, setCustomerInfo] = useState(null);
  const [errorKind, setErrorKind] = useState(ERROR_NETWORK);
  const [thanks, setThanks] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [retrying, setRetrying] = useState(false);

  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = loadStore(customer, surveyId);

  /* Travamentos separados de proposito. Se o toque disputasse o mesmo cadeado
   * da manutencao, um flush lento (a rede pode pendurar por HTTP_TIMEOUT_MS)
   * deixaria o totem surdo — justamente na rede instavel que a fila existe para
   * cobrir. O toque sempre ganha; a manutencao e que espera. */
  const tapBusyRef = useRef(false);
  const maintenanceBusyRef = useRef(false);
  const bootstrapBusyRef = useRef(false);
  const screenRef = useRef("boot");

  screenRef.current = screen;

  const commit = useCallback((next) => {
    storeRef.current = next;
    saveStore(next);
  }, []);

  /* Devolve um requestId da reserva ou, faltando, cria um na hora. */
  const nextRequestId = useCallback(async () => {
    const { store, requestId } = poolPop(storeRef.current);
    if (requestId) {
      commit(store);
      return requestId;
    }

    const created = await createRequest(customer, surveyId);
    if (created.result !== KIOSK_OK) return "";

    commit(setQuestion(storeRef.current, created.question));
    return created.requestId;
  }, [customer, surveyId]);

  const refillPool = useCallback(async () => {
    while (storeRef.current.pool.length < poolTarget) {
      const created = await createRequest(customer, surveyId);

      if (created.result === KIOSK_ERR_CLOSED) return KIOSK_ERR_CLOSED;
      if (created.result !== KIOSK_OK) return created.result;

      commit(poolPush(setQuestion(storeRef.current, created.question), created.requestId));
      if (created.question) setQuestionText(created.question);
    }

    return KIOSK_OK;
  }, [customer, surveyId, poolTarget]);

  const flushOutbox = useCallback(async () => {
    let entry = outboxPeek(storeRef.current);

    while (entry) {
      /* O flush mexe na cabeca da fila e o toque na cauda. Com uma entrada so
       * as duas pontas sao a mesma, e a nota sairia duas vezes. */
      if (tapBusyRef.current) return;

      let requestId = entry.requestId;

      if (!requestId) {
        requestId = await nextRequestId();
        if (!requestId) return;
        commit(outboxRehomeFirst(storeRef.current, requestId));
      }

      const sent = await sendRatingResilient(customer, requestId, entry.rating, nextRequestId);

      if (sent.result === KIOSK_OK || sent.result === KIOSK_ERR_ALREADY) {
        commit(outboxDropFirst(storeRef.current));
        entry = outboxPeek(storeRef.current);
        continue;
      }

      if (sent.requestId !== entry.requestId) {
        commit(outboxRehomeFirst(storeRef.current, sent.requestId));
      }
      return;
    }
  }, [customer, nextRequestId]);

  const runBootstrap = useCallback(async () => {
    let persistedQuestion = storeRef.current.question;
    if (persistedQuestion) setQuestionText(persistedQuestion);

    try {
      const surveys = await fetchSurveys(customer);
      const survey = pickSurvey(surveys, surveyId);

      /* Sem pesquisa utilizavel a nota nao tem onde cair — e configuracao, nao
       * rede, e a tela precisa dizer isso para nao mandar o lojista mexer no
       * roteador. */
      if (!survey || !survey.allowAutoCreateRequests) {
        setErrorKind(ERROR_CONFIG);
        setScreen("error");
        return;
      }

      if (survey.npsQuestion) {
        persistedQuestion = survey.npsQuestion;
        commit(setQuestion(storeRef.current, survey.npsQuestion));
        setQuestionText(survey.npsQuestion);
      }
    } catch (err) {
      /* Sem rede no boot mas com pergunta em cache, o totem coleta assim mesmo:
       * a nota entra na fila sem solicitacao e o flush a reancora depois. */
      if (!persistedQuestion) {
        setErrorKind(ERROR_NETWORK);
        setScreen("error");
        return;
      }
    }

    const filled = await refillPool();
    if (filled === KIOSK_ERR_CLOSED) {
      setErrorKind(ERROR_CONFIG);
      setScreen("error");
      return;
    }

    if (!storeRef.current.pool.length && !storeRef.current.question) {
      setErrorKind(ERROR_NETWORK);
      setScreen("error");
      return;
    }

    setScreen("scale");
  }, [customer, surveyId, refillPool]);

  /* A tela de erro reagenda a cada 15 s, mas um bootstrap com a rede pendurada
   * leva bem mais que isso (fetchSurveys + refillPool, cada chamada ate
   * HTTP_TIMEOUT_MS). Sem esta guarda eles se empilham indefinidamente. */
  const bootstrap = useCallback(async () => {
    if (bootstrapBusyRef.current) return;
    bootstrapBusyRef.current = true;
    setRetrying(true);

    try {
      await runBootstrap();
    } finally {
      bootstrapBusyRef.current = false;
      setRetrying(false);
    }
  }, [runBootstrap]);

  const handleRating = useCallback(
    async (rating) => {
      /* Um segundo toque durante o processamento do primeiro e descartado, que
       * e o comportamento desejado num totem. */
      if (tapBusyRef.current) return;
      tapBusyRef.current = true;

      const popped = poolPop(storeRef.current);
      const reserved = popped.requestId;
      const store = reserved ? popped.store : storeRef.current;

      /* Persistir antes de qualquer coisa: um reload agora nao pode custar a
       * nota. */
      const queued = outboxEnqueue(store, reserved, rating);
      commit(queued.store);

      setThanks({ rating, qrUrl: buildSurveyUrl(customer, reserved, rating) });
      setSeconds(countdownSeconds);
      setScreen("thanks");

      try {
        let requestId = reserved;

        if (!requestId) {
          requestId = await nextRequestId();
          if (!requestId) return; /* fica na fila ate a rede voltar */
          if (queued.enqueued) commit(outboxRehomeLast(storeRef.current, requestId));
        }

        const sent = await sendRatingResilient(customer, requestId, rating, nextRequestId);

        /* Fila cheia: a nota nao chegou a entrar, entao nao ha entrada na cauda
         * para dar baixa — mexer nela apagaria a nota de outra pessoa. */
        if (queued.enqueued) {
          if (sent.result === KIOSK_OK || sent.result === KIOSK_ERR_ALREADY) {
            commit(outboxDropLast(storeRef.current));
          } else {
            commit(outboxRehomeLast(storeRef.current, sent.requestId));
          }
        }

        /* O QR pode ter mudado de recipiente no meio do caminho. Reescreve a
         * imagem sem reiniciar a contagem. */
        if (sent.requestId && sent.requestId !== reserved) {
          const url = buildSurveyUrl(customer, sent.requestId, rating);
          setThanks((prev) => (prev ? { ...prev, qrUrl: url } : prev));
        }
      } finally {
        tapBusyRef.current = false;
      }
    },
    [customer, countdownSeconds, nextRequestId]
  );

  const backToScale = useCallback(() => {
    setThanks(null);
    setScreen("scale");
  }, []);

  useEffect(() => {
    document.body.classList.toggle("kiosk", true);

    const blockContextMenu = (event) => event.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu);

    /* Trava o zoom so nesta rota: bloquear pinch nas paginas de consumidor
     * seria regressao de acessibilidade. */
    const viewport = document.querySelector('meta[name="viewport"]');
    const previousViewport = viewport?.getAttribute("content");
    viewport?.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );

    return () => {
      document.body.classList.toggle("kiosk", false);
      document.removeEventListener("contextmenu", blockContextMenu);
      if (previousViewport) viewport.setAttribute("content", previousViewport);
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/${customer}/info`)
      .then((response) => setCustomerInfo(response.data))
      .catch(() => setCustomerInfo(null));
  }, [customer]);

  useEffect(() => {
    const locale = forcedLanguage || customerInfo?.settings?.locale;
    if (locale) i18n.changeLanguage(locale);
  }, [customerInfo, forcedLanguage]);

  useEffect(() => {
    /* Trocar de loja ou de pesquisa descarta reserva e fila: aquelas
     * solicitacoes pertencem a configuracao anterior. */
    storeRef.current = loadStore(customer, surveyId);
    bootstrap();
  }, [customer, surveyId]);

  /* Contagem da tela de agradecimento. O retorno a escala fica num efeito
   * proprio: disparar setScreen de dentro do updater do setSeconds e efeito
   * colateral em funcao que o React pode reexecutar. */
  useEffect(() => {
    if (screen !== "thanks") return undefined;

    const timer = setInterval(() => setSeconds((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [screen]);

  useEffect(() => {
    if (screen === "thanks" && seconds === 0) backToScale();
  }, [screen, seconds, backToScale]);

  /* Ciclo ocioso: drena pendencias e repoe a reserva. Nao mexe na tela do QR
   * com o usuario ainda lendo. */
  useEffect(() => {
    if (screen !== "scale") return undefined;

    const maintain = async () => {
      if (maintenanceBusyRef.current || tapBusyRef.current) return;
      if (screenRef.current !== "scale") return;

      maintenanceBusyRef.current = true;
      try {
        await flushOutbox();
        const filled = await refillPool();

        /* Pesquisa desativada com o totem ja em operacao: sem reserva, todo
         * toque viraria nota sem solicitacao e sem QR, em silencio. O operador
         * precisa ver isso — a tela de erro se resolve sozinha quando a
         * pesquisa voltar. */
        if (filled === KIOSK_ERR_CLOSED && !storeRef.current.pool.length) {
          setErrorKind(ERROR_CONFIG);
          setScreen("error");
        }
      } finally {
        maintenanceBusyRef.current = false;
      }
    };

    maintain();
    const timer = setInterval(maintain, MAINTENANCE_MS);
    window.addEventListener("online", maintain);

    return () => {
      clearInterval(timer);
      window.removeEventListener("online", maintain);
    };
  }, [screen, flushOutbox, refillPool]);

  /* A tela de erro se resolve sozinha quando a causa passa. */
  useEffect(() => {
    if (screen !== "error") return undefined;

    const timer = setInterval(bootstrap, ERROR_RETRY_MS);
    return () => clearInterval(timer);
  }, [screen, bootstrap]);

  const brand = customerInfo?.logo ? (
    <img
      className="kiosk-logo"
      src={customerInfo.logo}
      alt={t("alt.customer_logo", { name: customerInfo.name })}
    />
  ) : (
    <span className="kiosk-logo-text">{customerInfo?.name || ""}</span>
  );

  if (screen === "boot") {
    return (
      <div className="kiosk-page kiosk-boot">
        {brand}
        <p className="kiosk-boot-title">{t("kiosk.connecting")}</p>
        <div className="kiosk-progress">
          <span />
        </div>
      </div>
    );
  }

  if (screen === "error") {
    const isConfig = errorKind === ERROR_CONFIG;

    return (
      <div className="kiosk-page kiosk-error">
        {brand}
        <h1>{t(isConfig ? "kiosk.error_config_title" : "kiosk.error_network_title")}</h1>
        <p>{t(isConfig ? "kiosk.error_config_body" : "kiosk.error_network_body")}</p>
        {/* Uma tentativa com a rede pendurada leva mais de um minuto. Sem este
            estado o botao pareceria morto e o operador concluiria que o totem
            travou. */}
        <button type="button" className="kiosk-retry" onClick={bootstrap} disabled={retrying}>
          {t(retrying ? "kiosk.retrying" : "kiosk.retry")}
        </button>
      </div>
    );
  }

  if (screen === "thanks" && thanks) {
    return (
      <div className="kiosk-page kiosk-thanks" onClick={backToScale}>
        <div className="kiosk-thanks-text">
          <p className={`kiosk-pill kiosk-pill-${ratingBand(thanks.rating)}`}>
            <span className="kiosk-pill-score">{thanks.rating}</span>
            {t("kiosk.rating_saved")}
          </p>

          <h1>{t("kiosk.thanks")}</h1>
          <p className="kiosk-thanks-hint">
            {t(thanks.qrUrl ? "kiosk.qr_hint" : "kiosk.saved_offline")}
          </p>

          <p className="kiosk-countdown">{t("kiosk.returning_in", { seconds })}</p>
        </div>

        {thanks.qrUrl && (
          <div className="kiosk-qr">
            <div className="kiosk-qr-card">
              <QRCodeSVG value={thanks.qrUrl} size={256} fgColor="#211a44" bgColor="#ffffff" />
            </div>
            <p className="kiosk-qr-caption">{t("kiosk.qr_caption")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="kiosk-page kiosk-collect">
      <header className="kiosk-header">
        {brand}
        <span className="kiosk-eyebrow">{t("kiosk.eyebrow")}</span>
      </header>

      {/* Pergunta e escala formam um bloco unico e centralizado. Deixar a
          pergunta absorver o espaco livre gruda os chips na borda inferior, o
          que num totem tambem e problema de alcance: o dedo bate no bisel. */}
      <div className="kiosk-collect-main">
        <h1 className="kiosk-question">{question || t("kiosk.question_fallback")}</h1>

        <KioskScale onPick={handleRating} />
      </div>
    </div>
  );
}
